import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import CylinderList from "@/pages/CylinderList";
import CylinderDetail from "@/pages/CylinderDetail";
import CreateCylinder from "@/pages/CreateCylinder";
import Kanban from "@/pages/Kanban";
import DesignQueue from "@/pages/engineering/DesignQueue";
import EngineeringDetail from "@/pages/engineering/EngineeringDetail";
import LifecycleList from "@/pages/lifecycle/LifecycleList";
import LifecycleDetail from "@/pages/lifecycle/LifecycleDetail";
import ReceivingList from "@/pages/receiving/ReceivingList";
import ReceivingForm from "@/pages/receiving/ReceivingForm";
import ReceivingDetail from "@/pages/receiving/ReceivingDetail";
import ImportCentre from "@/pages/admin/ImportCentre";
import SystemMaintenance from "@/pages/admin/SystemMaintenance";
import UserManagement from "@/pages/admin/UserManagement";
import Reports from "@/pages/Reports";
import SupplierPoList from "@/pages/commercial/SupplierPoList";
import SupplierPoDetail from "@/pages/commercial/SupplierPoDetail";
import MyProfile from "@/pages/profile/MyProfile";
import ChangePassword from "@/pages/profile/ChangePassword";

// Commercial workflow (Module 2)
import InquiryList from "@/pages/commercial/InquiryList";
import InquiryForm from "@/pages/commercial/InquiryForm";
import InquiryDetail from "@/pages/commercial/InquiryDetail";
import QuotationList from "@/pages/commercial/QuotationList";
import QuotationForm from "@/pages/commercial/QuotationForm";
import QuotationDetail from "@/pages/commercial/QuotationDetail";
import PoList from "@/pages/commercial/PoList";
import PoForm from "@/pages/commercial/PoForm";
import PoDetail from "@/pages/commercial/PoDetail";
import OaList from "@/pages/commercial/OaList";
import OaForm from "@/pages/commercial/OaForm";
import OaDetail from "@/pages/commercial/OaDetail";

// Role-based landing page (user-choice C):
//  Admin / Sales / Plant Head / Finance → Reports
//  Production floor roles → Lifecycle Tracker
const PRODUCTION_ROLES = new Set([
  "production_supervisor", "quality_inspector", "design_engineer",
  "design_hod", "purchase", "dispatch",
]);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-blue-900" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function RoleLanding() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (PRODUCTION_ROLES.has(user.role)) return <Navigate to="/lifecycle" replace />;
  return <Navigate to="/reports" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />

            {/* Commercial */}
            <Route path="/inquiries" element={<ProtectedRoute><InquiryList /></ProtectedRoute>} />
            <Route path="/inquiries/new" element={<ProtectedRoute><InquiryForm /></ProtectedRoute>} />
            <Route path="/inquiries/:id" element={<ProtectedRoute><InquiryDetail /></ProtectedRoute>} />
            <Route path="/inquiries/:id/edit" element={<ProtectedRoute><InquiryForm /></ProtectedRoute>} />

            <Route path="/quotations" element={<ProtectedRoute><QuotationList /></ProtectedRoute>} />
            <Route path="/quotations/new" element={<ProtectedRoute><QuotationForm /></ProtectedRoute>} />
            <Route path="/quotations/:id" element={<ProtectedRoute><QuotationDetail /></ProtectedRoute>} />

            <Route path="/purchase-orders" element={<ProtectedRoute><PoList /></ProtectedRoute>} />
            <Route path="/purchase-orders/new" element={<ProtectedRoute><PoForm /></ProtectedRoute>} />
            <Route path="/purchase-orders/:id" element={<ProtectedRoute><PoDetail /></ProtectedRoute>} />

            <Route path="/order-acceptances" element={<ProtectedRoute><OaList /></ProtectedRoute>} />
            <Route path="/order-acceptances/new" element={<ProtectedRoute><OaForm /></ProtectedRoute>} />
            <Route path="/order-acceptances/:id" element={<ProtectedRoute><OaDetail /></ProtectedRoute>} />

            {/* Supplier POs — deep-link routes kept for legacy links; nav entry removed per business decision */}
            <Route path="/supplier-pos" element={<ProtectedRoute><SupplierPoList /></ProtectedRoute>} />
            <Route path="/supplier-pos/:id" element={<ProtectedRoute><SupplierPoDetail /></ProtectedRoute>} />

            {/* Manufacturing */}
            <Route path="/cylinders" element={<ProtectedRoute><CylinderList /></ProtectedRoute>} />
            <Route path="/cylinders/create" element={<ProtectedRoute><CreateCylinder /></ProtectedRoute>} />
            <Route path="/cylinders/:id" element={<ProtectedRoute><CylinderDetail /></ProtectedRoute>} />
            <Route path="/kanban" element={<ProtectedRoute><Kanban /></ProtectedRoute>} />
            <Route path="/engineering" element={<ProtectedRoute><DesignQueue /></ProtectedRoute>} />
            <Route path="/engineering/:id" element={<ProtectedRoute><EngineeringDetail /></ProtectedRoute>} />
            <Route path="/lifecycle" element={<ProtectedRoute><LifecycleList /></ProtectedRoute>} />
            <Route path="/lifecycle/:id" element={<ProtectedRoute><LifecycleDetail /></ProtectedRoute>} />
            <Route path="/receiving" element={<ProtectedRoute><ReceivingList /></ProtectedRoute>} />
            <Route path="/receiving/new" element={<ProtectedRoute><ReceivingForm /></ProtectedRoute>} />
            <Route path="/receiving/:id" element={<ProtectedRoute><ReceivingDetail /></ProtectedRoute>} />

            <Route path="/admin/import-centre" element={<ProtectedRoute><ImportCentre /></ProtectedRoute>} />
            <Route path="/admin/system-maintenance" element={<ProtectedRoute><SystemMaintenance /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
            <Route path="/profile/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

            {/* Legacy redirects — old dashboards removed */}
            <Route path="/dashboard" element={<Navigate to="/reports" replace />} />
            <Route path="/management-dashboard" element={<Navigate to="/reports" replace />} />

            <Route path="/" element={<RoleLanding />} />
            <Route path="*" element={<RoleLanding />} />
          </Routes>
          </ErrorBoundary>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
